/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Evolvable, evolve, pickBy } from 'ramda';

type TransformerFn = (value: any) => any;

type TransformMode = 'forLabel' | 'forFormValue';

type SplitField = {
  forLabel?: TransformerFn;
  forFormValue?: TransformerFn;
};

type FieldDef = TransformerFn | SplitField;

type EvolvedStrict<E extends Record<string, TransformerFn>> = {
  [k in keyof E]: ReturnType<E[k]>;
}

const evolveStrict = <E extends Record<string, TransformerFn>>(
  transformations: E,
): <V extends Evolvable<E>>(obj: V) => EvolvedStrict<E> => (obj) => pickBy(
  (_, key) => key in transformations,
)(evolve(transformations, obj));

export type FieldTransformers<TEntity> = {
  [K in keyof TEntity]?:
  | ((value: TEntity[K]) => unknown)
  | {
    forLabel?: (value: TEntity[K]) => unknown;
    forFormValue?: (value: TEntity[K]) => unknown;
  };
};

/**
 * Extracts the transformer function for a field in a given mode.
 * - Simple function → itself (used for all modes)
 * - Split object with the requested mode → that mode's function
 * - Split object without the requested mode → `never` (excluded)
 */
type ResolveFieldFn<F, Mode extends TransformMode> =
  F extends TransformerFn
    ? F
    : F extends Record<Mode, infer Fn extends TransformerFn>
      ? Fn
      : never;

/** Strict-mode transform result: only fields that have a resolver for the mode. */
type StrictTransformResult<
  TFields extends Record<string, FieldDef>,
  Mode extends TransformMode,
> = {
  [K in keyof TFields as
  [ResolveFieldFn<TFields[K], Mode>] extends [never]
    ? never
    : K
  ]: ReturnType<ResolveFieldFn<TFields[K], Mode>>;
};

/** Per-field accessors with NonNullable stripping on params and return types. */
type FieldAccessors<
  TFields extends Record<string, FieldDef>,
  Mode extends TransformMode,
> = {
  [K in keyof TFields as
  [ResolveFieldFn<TFields[K], Mode>] extends [never]
    ? never
    : K
  ]: ResolveFieldFn<TFields[K], Mode> extends
  (value: infer V) => infer R
    ? (value: NonNullable<V>) => NonNullable<R>
    : never;
};

const resolveForMode = (
  fields: Record<string, FieldDef>,
  mode: TransformMode,
): Record<string, TransformerFn> => {
  const result: Record<string, TransformerFn> = {};
  for (const key of Object.keys(fields)) {
    const value = fields[key];
    if (typeof value === 'function') {
      result[key] = value;
    } else if (value[mode]) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      result[key] = value[mode]!;
    }
  }
  return result;
};

/**
 * Creates a field adapter with per-field transformers.
 *
 * Each field can be a simple function (used for all modes)
 * or a `{ forLabel, forFormValue }` object with mode-specific transforms.
 *
 * @param options - `fields` and optional `meta` methods.
 * @param mode - `'strict'` keeps only transformed keys;
 *   `'loose'` passes unknown keys through.
 *
 * @example
 * const adapter = defineFieldAdapter({
 *   fields: {
 *     status: formatEnumValue(StatusEnum),
 *     totalBudget: {
 *       forLabel: rPiped(toUpper, formatNumber('currency')),
 *       forFormValue: toUpperDenomination,
 *     },
 *   },
 *   meta: {},
 * }, 'strict');
 *
 * adapter.transform(data)                // default 'forLabel'
 * adapter.transform(data, 'forFormValue')
 * adapter.forLabel.status(value)
 */
export function defineFieldAdapter <
  TFields extends Record<string, FieldDef>,
  TMeta extends Record<string, TransformerFn>,
> (
  options: { fields: TFields; meta?: TMeta },
  mode: 'strict',
): {
  transform: <M extends TransformMode = 'forLabel'>(
    obj: any,
    mode?: M,
  ) => StrictTransformResult<TFields, M>;
  forLabel: FieldAccessors<TFields, 'forLabel'>;
  forFormValue: FieldAccessors<TFields, 'forFormValue'>;
} & TMeta;

export function defineFieldAdapter <
  TFields extends Record<string, TransformerFn>,
  TMeta extends Record<string, TransformerFn>,
> (
  options: { fields: TFields; meta?: TMeta },
  mode: 'loose',
): {
  transform: ReturnType<typeof evolve<TFields>>;
  forLabel: FieldAccessors<TFields, 'forLabel'>;
  forFormValue: FieldAccessors<TFields, 'forFormValue'>;
} & TMeta;

export function defineFieldAdapter (
  {
    fields,
    meta = {},
  }: {
    fields: Record<string, FieldDef>;
    meta?: Record<string, TransformerFn>;
  },
  mode: 'loose' | 'strict',
): any {
  const forLabelResolved = resolveForMode(fields, 'forLabel');
  const forFormValueResolved = resolveForMode(fields, 'forFormValue');

  const createTransform = (
    transformers: Record<string, TransformerFn>,
  ): TransformerFn => (
    mode === 'strict'
      ? evolveStrict(transformers)
      : evolve(transformers)
  ) as TransformerFn;

  const forLabelTransform = createTransform(forLabelResolved);
  const forFormValueTransform = createTransform(forFormValueResolved);

  const strictTransform = (
    obj: any,
    transformMode: TransformMode = 'forLabel',
  ): any => (
    transformMode === 'forFormValue'
      ? forFormValueTransform(obj)
      : forLabelTransform(obj)
  );

  return {
    transform: mode === 'strict'
      ? strictTransform
      : forLabelTransform,
    forLabel: forLabelResolved,
    forFormValue: forFormValueResolved,
    ...meta,
  };
}
